/**
 * Getting the photograph out.
 *
 * The share sheet is tried first, because on a phone that is the route to the
 * camera roll and to every app the person already uses. A download is the
 * fallback, and on desktop it is the whole story.
 */

export type ImageFormat = 'image/jpeg' | 'image/png' | 'image/webp';

const EXTENSION: Record<ImageFormat, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

export type ShareOutcome = 'shared' | 'downloaded' | 'cancelled';

export function toBlob(canvas: HTMLCanvasElement, format: ImageFormat, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('The photograph could not be encoded.'))),
      format,
      quality,
    );
  });
}

/** A stable, sortable filename: the moment it was taken, to the second. */
export function filenameFor(format: ImageFormat, takenAt = Date.now()): string {
  const d = new Date(takenAt);
  const p = (n: number) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
  return `second-person-${stamp}.${EXTENSION[format]}`;
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Hand a photograph to the system.
 *
 * `navigator.share` must be called synchronously enough that the browser still
 * counts it as user-activated, so the blob is encoded by the caller and passed
 * in ready to go.
 */
export async function shareImage(
  blob: Blob,
  format: ImageFormat,
  takenAt = Date.now(),
): Promise<ShareOutcome> {
  const filename = filenameFor(format, takenAt);
  const file = new File([blob], filename, { type: format });

  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
      return 'shared';
    } catch (err) {
      // A dismissed share sheet is not a failure, and should not then dump a
      // file into the downloads folder behind the person's back.
      if (err instanceof DOMException && err.name === 'AbortError') return 'cancelled';
    }
  }

  download(blob, filename);
  return 'downloaded';
}

/** Save without going through the share sheet. */
export function downloadImage(blob: Blob, format: ImageFormat, takenAt = Date.now()): void {
  download(blob, filenameFor(format, takenAt));
}
